import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Plus, Edit, Trash2, Save, X, Star, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

export default function MyAccountPage() {
  const { user, logout, loading, token } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loadingAddr, setLoadingAddr] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: 'Domů', firstname: '', lastname: '', company: '', address1: '', city: '', postcode: '', country: 'Česká republika', phone: '', is_default: false });

  useEffect(() => { if (!loading && !user) navigate('/prihlaseni'); }, [user, navigate, loading]);

  const loadAddresses = useCallback(() => {
    if (!token) return;
    setLoadingAddr(true);
    axios.get(`${API}/user/addresses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAddresses(res.data))
      .catch(() => {})
      .finally(() => setLoadingAddr(false));
  }, [token]);

  useEffect(() => { loadAddresses(); }, [loadAddresses]);

  const resetForm = () => {
    setForm({ label: 'Domů', firstname: user?.firstname || '', lastname: user?.lastname || '', company: '', address1: '', city: '', postcode: '', country: 'Česká republika', phone: '', is_default: false });
    setEditingId(null);
  };

  const startEdit = (addr) => {
    setEditingId(addr.id);
    setForm({ label: addr.label || '', firstname: addr.firstname || '', lastname: addr.lastname || '', company: addr.company || '', address1: addr.address1 || '', city: addr.city || '', postcode: addr.postcode || '', country: addr.country || 'Česká republika', phone: addr.phone || '', is_default: addr.is_default || false });
    setShowForm(true);
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim() || !form.address1.trim() || !form.city.trim() || !form.postcode.trim()) {
      toast.error('Vyplňte všechna povinná pole'); return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/user/addresses/${editingId}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Adresa uložena');
      } else {
        await axios.post(`${API}/user/addresses`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Adresa přidána');
      }
      setShowForm(false);
      resetForm();
      loadAddresses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba ukládání'); }
    finally { setSaving(false); }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm('Opravdu smazat adresu?')) return;
    try {
      await axios.delete(`${API}/user/addresses/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Adresa smazána');
      loadAddresses();
    } catch { toast.error('Chyba mazání'); }
  };

  const setDefault = async (id) => {
    try {
      await axios.patch(`${API}/user/addresses/${id}/default`, {}, { headers: { Authorization: `Bearer ${token}` } });
      loadAddresses();
    } catch { toast.error('Chyba nastavení'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div>;
  if (!user) return null;

  const inputCls = "w-full h-11 border border-slate-300 rounded px-4 focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="my-account-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Můj účet</h1>
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-semibold text-slate-800 mb-4">Osobní údaje</h2>
          <div className="space-y-3">
            <div><span className="text-sm text-slate-500">Jméno:</span><p className="font-medium">{user.firstname} {user.lastname}</p></div>
            <div><span className="text-sm text-slate-500">E-mail:</span><p className="font-medium">{user.email}</p></div>
            {user.company && (<div><span className="text-sm text-slate-500">Firma:</span><p className="font-medium">{user.company}</p></div>)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="font-semibold text-slate-800 mb-4">Rychlé odkazy</h2>
          <div className="space-y-2">
            <Link to="/objednavky" className="block py-2 px-4 bg-slate-50 rounded hover:bg-slate-100 transition-colors">Moje objednávky</Link>
            <Link to="/kosik" className="block py-2 px-4 bg-slate-50 rounded hover:bg-slate-100 transition-colors">Nákupní košík</Link>
            {user.is_admin && (<Link to="/admin" className="block py-2 px-4 bg-[#004E98]/10 text-[#004E98] rounded hover:bg-[#004E98]/20 transition-colors">Administrace</Link>)}
            <button onClick={logout} className="w-full text-left py-2 px-4 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors">Odhlásit se</button>
          </div>
        </div>
      </div>

      {/* Address book */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-800" data-testid="address-book-title">Adresář</h2>
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="h-10 px-4 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] transition-colors" data-testid="add-address-btn">
            <Plus className="w-4 h-4" /> Nová adresa
          </button>
        </div>

        {showForm && (
          <form onSubmit={saveAddress} className="bg-white border border-slate-200 rounded-md p-6 mb-4" data-testid="address-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Označení adresy</label>
                <input type="text" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} className={inputCls} placeholder="Domů / Práce / ..." data-testid="address-label-input" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Jméno *</label>
                <input type="text" required value={form.firstname} onChange={e => setForm({ ...form, firstname: e.target.value })} className={inputCls} data-testid="address-firstname-input" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Příjmení *</label>
                <input type="text" required value={form.lastname} onChange={e => setForm({ ...form, lastname: e.target.value })} className={inputCls} data-testid="address-lastname-input" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Firma</label>
                <input type="text" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Telefon</label>
                <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
              <div className="md:col-span-2"><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Ulice a číslo *</label>
                <input type="text" required value={form.address1} onChange={e => setForm({ ...form, address1: e.target.value })} className={inputCls} data-testid="address-street-input" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Město *</label>
                <input type="text" required value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="address-city-input" /></div>
              <div><label className="text-xs font-bold uppercase text-slate-500 mb-1 block">PSČ *</label>
                <input type="text" required value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} className={inputCls} data-testid="address-postcode-input" /></div>
              <div className="flex items-center gap-2 pt-4">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm font-medium">Nastavit jako výchozí adresu</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button type="submit" disabled={saving} className="h-10 px-5 bg-[#004E98] text-white rounded text-sm font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="save-address-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingId ? 'Uložit' : 'Přidat adresu'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="h-10 px-5 border border-slate-300 rounded text-sm hover:bg-slate-50">Zrušit</button>
            </div>
          </form>
        )}

        {loadingAddr ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#004E98]" /></div> : addresses.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-white border border-slate-200 rounded-md">Zatím nemáte uložené žádné adresy</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {addresses.map(addr => (
              <div key={addr.id} className={`bg-white border rounded-md p-5 relative ${addr.is_default ? 'border-[#004E98] ring-1 ring-[#004E98]/20' : 'border-slate-200'}`} data-testid={`address-card-${addr.id}`}>
                {addr.is_default && <span className="absolute top-3 right-3 text-xs bg-[#004E98] text-white px-2 py-0.5 rounded font-medium">Výchozí</span>}
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="font-semibold text-slate-800">{addr.label || 'Adresa'}</span>
                </div>
                <p className="text-sm text-slate-600">{addr.firstname} {addr.lastname}</p>
                {addr.company && <p className="text-sm text-slate-500">{addr.company}</p>}
                <p className="text-sm text-slate-600">{addr.address1}</p>
                <p className="text-sm text-slate-600">{addr.postcode} {addr.city}</p>
                {addr.phone && <p className="text-sm text-slate-500">{addr.phone}</p>}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <button onClick={() => startEdit(addr)} className="p-1.5 text-[#004E98] hover:bg-blue-50 rounded" title="Upravit" data-testid={`edit-address-${addr.id}`}><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteAddress(addr.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Smazat" data-testid={`delete-address-${addr.id}`}><Trash2 className="w-4 h-4" /></button>
                  {!addr.is_default && <button onClick={() => setDefault(addr.id)} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded" title="Nastavit jako výchozí" data-testid={`default-address-${addr.id}`}><Star className="w-4 h-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
